import { useMemo } from 'react'

import { compact } from 'lodash'

import { Group, Skeleton } from '~/components/component-library'
import {
  DetailsDropdownOption,
  SecondaryDisplayButtons,
} from '~/components/txn-display/SecondaryDisplayButtons'
import { useNetworkUpdates } from '~/components/txn-display/state-change/hooks/useNetworkUpdates'
import { useRecoveryUpdate } from '~/components/txn-display/state-change/hooks/useRecoveryUpdate'
import { useSignerUpdates } from '~/components/txn-display/state-change/hooks/useSignerUpdates'
import { runTenderlySimulation } from '~/components/txn-display/state-change/utils/simulation'
import { getChain } from '~/constants/chains'
import { BackendTransaction } from '~/types'
import { getChainLabel } from '~/utils/chainLabels'
import { cn } from '~/utils/tailwind-utils'
import { getTxnDetailsUrl } from '~/utils/tenderly'

export const MerkleStateChangeDisplay = ({
  transaction,
}: {
  transaction: BackendTransaction
}) => {
  const networkUpdates = useNetworkUpdates({ transaction })
  const signerUpdates = useSignerUpdates({ transaction })
  const recoveryUpdate = useRecoveryUpdate({ transaction })

  const contents = [...networkUpdates, ...signerUpdates, ...recoveryUpdate]

  // Build details options for child transactions (links for completed, simulations for pending)
  const detailsOptions = useMemo((): DetailsDropdownOption[] | undefined => {
    if (!transaction.isMerkleTransaction) return undefined
    const options = compact(
      transaction.childTransactions.map(
        (child): DetailsDropdownOption | undefined => {
          if (child.transactionHash) {
            const url = getTxnDetailsUrl({
              chainId: child.chainId,
              txnHash: child.transactionHash,
              userOpHash: child.userOpHash,
            })
            if (!url) return undefined
            return {
              type: 'link',
              label: getChainLabel(child.chainId),
              url,
            }
          }
          if (!getChain(child.chainId)?.supportsTenderly) return undefined
          return {
            type: 'simulation',
            label: getChainLabel(child.chainId),
            runSimulation: () =>
              runTenderlySimulation({
                transactionId: child.id,
                smartAccountAddress: child.smartAccountAddress,
                chainId: child.chainId,
                calls: child.calls.map((call) => ({
                  ...call,
                  value: BigInt(call.value),
                })),
              }),
          }
        },
      ),
    )
    return options.length > 0 ? options : undefined
  }, [transaction])

  const secondaryDisplayButtons = detailsOptions ? (
    <SecondaryDisplayButtons
      isShowingCalls={false}
      onToggleCalls={() => {}}
      hasDecodedCalls={false}
      detailsOptions={detailsOptions}
    />
  ) : undefined

  return (
    <Group label="Account updates" secondaryDisplay={secondaryDisplayButtons}>
      <div className="relative flex flex-col gap-1 rounded-lg bg-white px-3.5 py-3 text-sm ring-1 ring-inset ring-gray-200 dark:divide-gray-700 dark:bg-gray-900 dark:ring-gray-700">
        {contents.map(({ title, icon, isLoading, description }, index) => {
          return isLoading ? (
            <Skeleton className="h-10 w-full" key={index} />
          ) : (
            <div
              key={index}
              className={cn(
                'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2',
                index > 0 &&
                  'mt-1 border-t border-gray-200 pt-2 dark:border-gray-800',
              )}
            >
              <div className="flex items-center py-0.5 [&>svg]:size-4">
                {icon}
              </div>
              <div className="flex w-full min-w-0 flex-col gap-1.5">
                <span className="block w-full truncate text-base leading-5 text-primary">
                  {title}
                </span>
                {description && (
                  <span className="block min-h-4 w-full truncate text-sm leading-4 text-secondary">
                    {description}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </Group>
  )
}
